import React from 'react';
import Main from './main';
import History from './history';
import axios from '../axios';
import PlaceReservation from './place_reservation';
import EntryScreen from './entry';
import ModalAlert from '../redux/components/modal/modal_alert';
import ModalConfirm from '../redux/components/modal/modal_confirm';
import {
  initialPage,
  historyDestination,
  INITIAL_SUGGEST,
  nextSuggestionState,
  genreDraftFor,
  applyEntryDraftState,
  changeEntryTitleState,
  shouldConfirmOverwrite,
  saveEntryTitle,
  generateEntryDraft,
  markAiAcceptedState,
} from './entry_logic';
import {
  shouldGenerateDefaultImage,
  requestDefaultImage,
  withDefaultImage,
} from './default_image';

const EventEdit = (props) => {
  // 'entry' | 'basic' | 'detail' | 1 (History) | 3 (PlaceReservation) | 0 (フラグ OFF の1本フォーム)
  const [page, setPage] = React.useState(() =>
    initialPage({
      entryFlowEnabled: props.entryFlowEnabled,
      isNew: props.isNew,
      title: props.event.title,
    }),
  );
  // 会場予約から戻る先。フラグ OFF では 0（従来の1本フォーム）のまま維持される
  const [returnPage, setReturnPage] = React.useState('basic');
  const [historyOriginPage, setHistoryOriginPage] = React.useState('basic');
  const [historyEventId, setHistoryEventId] = React.useState(null);
  const [events, setEvents] = React.useState([]);
  const [event, setEvent] = React.useState(props.event);
  const [circle, setCircle] = React.useState(props.circle);
  const [usersCircles, setUsersCircles] = React.useState(
    props.usersCircles || [],
  );
  const [eventsPlans, setEventsPlans] = React.useState(props.eventsPlans || []);
  const [contents, setContents] = React.useState(props.contents || []);

  const [bodyText, setBodyText] = React.useState(() => {
    if (!props.contents) {
      return '';
    }
    const content = props.contents.find(
      (content) => content.content_type && content.content_type === 1,
    );
    return content ? content.body : '';
  });
  const isAlreadyReplacedFromTemplate = React.useRef(false);
  const [selectedPlace, setSelectedPlace] = React.useState(null);

  const [titleText, setTitleText] = React.useState(() => {
    if (!props.event.title) {
      return '';
    }
    return props.event.title;
  });
  // @context TGW-2195 手入力を補助機能が黙って消さないため、補助由来のタイトルと区別する。
  const [titleEditedByUser, setTitleEditedByUser] = React.useState(false);
  // @context TGW-2195 履歴画面などから入口へ戻って再マウントされても、入力中のフォーカスを再び奪わない。
  const entryAutoFocusPending = React.useRef(true);
  const overwriteConfirmModalRef = React.useRef(null);
  const pendingEntryDraftRef = React.useRef(null);

  const [reservationSummary, setReservationSummary] = React.useState(null);

  // @context TGW-2195 たたき台の由来（'ai' / 'genre' / 'copy' / null）と「AIの提案」ピルの表示。
  //   由来はバナー（F2-2）の出し分けに、ピルは題名・本文が AI 由来であることの表示に使う。
  const [draftSource, setDraftSource] = React.useState(null);
  const [aiSuggested, setAiSuggested] = React.useState({
    title: false,
    body: false,
  });
  const clearAiSuggested = React.useCallback((field) => {
    setAiSuggested((prev) =>
      prev[field] ? { ...prev, [field]: false } : prev,
    );
  }, []);
  const markAiAccepted = React.useCallback((field) => {
    setDraftSource(markAiAcceptedState({ aiSuggested: {} }, field).draftSource);
    setAiSuggested((prev) => markAiAcceptedState({ aiSuggested: prev }, field).aiSuggested);
  }, []);

  const [entrySaving, setEntrySaving] = React.useState(false);
  const [entryError, setEntryError] = React.useState('');
  const [aiGenerating, setAiGenerating] = React.useState(false);
  const [aiError, setAiError] = React.useState('');
  const [aiTheme, setAiTheme] = React.useState('');
  const aiRateLimitModalRef = React.useRef(null);
  // F1-8 で選んだジャンル名。カテゴリ付与ジョブへのヒントとして update_title に渡す
  const [genreLabel, setGenreLabel] = React.useState(null);
  const genreHint = React.useRef(null);
  const [suggest, dispatchSuggest] = React.useReducer(
    nextSuggestionState,
    INITIAL_SUGGEST,
  );

  const openHistoryFrom = React.useCallback((origin) => {
    setHistoryOriginPage(origin);
    setPage(1);
  }, []);

  const setPageFromMain = React.useCallback(
    (nextPage) => {
      if (nextPage === 1) {
        openHistoryFrom(page);
        return;
      }
      setPage(nextPage);
    },
    [openHistoryFrom, page],
  );

  // @context TGW-2195 補助結果をイベント名欄で確認・編集できるよう、保存せず入口 state へ反映する。
  //   後から別の補助を選んだ場合は、本文・由来・ジャンルヒントも最後に選んだ内容へ揃える。
  const applyEntryDraft = (draft) => {
    const next = applyEntryDraftState(
      {
        titleText,
        bodyText,
        draftSource,
        genre: genreHint.current,
        aiSuggested,
        error: entryError,
        aiError,
      },
      draft,
    );
    setTitleText(next.titleText);
    setBodyText(next.bodyText);
    genreHint.current = next.genre;
    setGenreLabel(next.genre);
    setDraftSource(next.draftSource);
    setAiSuggested(next.aiSuggested);
    setEntryError(next.error);
    setAiError(next.aiError);
    setTitleEditedByUser(false);
  };

  // @context TGW-2195 AI候補・ジャンル・AI生成結果は同じ確認経路を通し、承認前の下書きを一時保持する。
  const requestApplyEntryDraft = (draft) => {
    if (
      shouldConfirmOverwrite({
        title: titleText,
        editedByUser: titleEditedByUser,
      })
    ) {
      pendingEntryDraftRef.current = draft;
      overwriteConfirmModalRef.current &&
        overwriteConfirmModalRef.current.openModal();
      return;
    }
    applyEntryDraft(draft);
  };

  // @context TGW-2195 AI生成後の手修正では本文のAI由来表示を残し、編集したタイトルの表示だけを外す。
  const changeEntryTitle = (value) => {
    const next = changeEntryTitleState({ titleText, aiSuggested }, value);
    setTitleText(next.titleText);
    setAiSuggested(next.aiSuggested);
    setTitleEditedByUser(true);
  };

  // @context TGW-2195 入口の「次へ」をタイトルの確定点とし、この時だけ保存・遷移する。
  //   下書き保存 API はチケット必須の検査で 400 になるため使えず、専用の軽量 API を叩く。
  //   genre はカテゴリ付与ジョブへのヒント（F7-1）。サーバー側で cache に置かれる。
  const commitEntryTitle = async (title) => {
    if (entrySaving) return;
    const trimmed = (title || '').trim();
    if (!trimmed) return;
    setEntrySaving(true);
    setEntryError('');
    try {
      await saveEntryTitle({
        post: axios.post,
        eventId: event.id,
        title: trimmed,
        genre: genreHint.current,
        onSaved: (savedTitle) => {
          setEvent((prev) => ({ ...prev, title: savedTitle }));
          if (shouldGenerateDefaultImage(event, savedTitle)) {
            requestDefaultImage(event.id, savedTitle)
              .then((url) =>
                setEvent(withDefaultImage(url)),
              )
              .catch(() => {});
          }
          setPage('basic');
        },
        onFailed: () =>
          setEntryError('保存できませんでした。もう一度お試しください。'),
      });
    } finally {
      setEntrySaving(false);
    }
  };

  // F1-9 イベント名とは別の一言から題名・紹介文のたたき台を作り、入口へ反映する
  const generateFromTheme = async (theme) => {
    const trimmed = (theme || '').trim();
    if (!trimmed || aiGenerating || entrySaving) return;
    setAiGenerating(true);
    setAiError('');
    try {
      const result = await generateEntryDraft({
        post: axios.post,
        theme: trimmed,
      });
      if (result.status === 'success') {
        requestApplyEntryDraft(result.draft);
      } else if (result.status === 'rate_limited') {
        aiRateLimitModalRef.current && aiRateLimitModalRef.current.openModal();
      } else {
        setAiError('下書きを作れませんでした。もう一度お試しください。');
      }
    } finally {
      setAiGenerating(false);
    }
  };

  // F1-5 / F1-7 タイトル候補。材料はサーバーが組む（クライアントは event_id と除外リストだけ送る）
  const fetchSuggestions = (exclude = []) => {
    dispatchSuggest({ type: 'request' });
    axios
      .post(
        '/api/event/suggest_titles',
        JSON.stringify({ event_id: event.id, exclude }),
        { timeout: 5000 },
      )
      .then((response) =>
        dispatchSuggest({
          type: 'success',
          titles: response.data.titles,
          regen: exclude.length > 0,
        }),
      )
      .catch(() => dispatchSuggest({ type: 'failure' }));
  };

  React.useEffect(() => {
    if (page === 'entry' && suggest.status === 'idle') fetchSuggestions();
  }, [page]);

  React.useEffect(() => {
    if (props.circle.id) {
      axios
        .get(`/api/event/history_list/${props.circle.id}`)
        .then((response) => setEvents(response.data.history_list));
    }
  }, [props.circle.id]);

  // @context TGW-2195 Android WebView ではキーボード表示時に入力欄が隠れるため、既存フォームと同じ位置へスクロールする。
  const onFocusScrollForAndroid = React.useCallback(
    (focusEvent) => {
      if (props.userAgent === 'android' && window) {
        const rect = focusEvent.target.getBoundingClientRect();
        window.scrollTo({
          top: rect.top + window.scrollY - 30,
          behavior: 'smooth',
        });
      }
    },
    [props.userAgent],
  );

  const onEntryTitleFocus = React.useCallback(
    (focusEvent) => {
      entryAutoFocusPending.current = false;
      onFocusScrollForAndroid(focusEvent);
    },
    [onFocusScrollForAndroid],
  );

  React.useEffect(() => {
    if (props.isNew || reservationSummary) return;
    axios
      .get(`/api/places/reservation_detail/${props.event.id}`)
      .then((res) => {
        if (res.data)
          setReservationSummary({
            id: res.data.id,
            place_id: res.data.place_id,
            place_name: res.data.place_name,
            start_at: res.data.start_at,
            end_at: res.data.end_at,
            num_people: res.data.num_people,
            request_detail: res.data.request_detail,
            reserver_name: res.data.reserver_name,
            reserver_tel: res.data.reserver_tel,
          });
      })
      .catch(() => {});
  }, [props.isNew, props.event.id, reservationSummary]);

  const handleEditReservation = React.useCallback(() => {
    if (reservationSummary?.place_id) {
      setSelectedPlace({
        id: reservationSummary.place_id,
        name: reservationSummary.place_name,
      });
    }
    setReturnPage(page);
    setPage(3);
  }, [reservationSummary, page]);

  // テンプレートを読み込み
  React.useEffect(() => {
    if (
      !props.isNew ||
      isAlreadyReplacedFromTemplate.current ||
      props.eventsPlans.length > 0
    ) {
      return;
    }
    isAlreadyReplacedFromTemplate.current = true;

    const body = { event_id: props.event.id };
    if (props.useCircleTemplate) {
      body.use_circle_template = true;
    }
    axios
      .post('/api/event/replace_event_from_template', JSON.stringify(body))
      .then((response) => {
        const newData = JSON.parse(response.data.event);
        const dateKeys = [
          'event_date',
          'event_end_datetime',
          'application_due_date',
          'meeting_datetime',
        ];
        setEvent((prev) => {
          return {
            ...prev,
            ...Object.fromEntries(
              Object.entries(newData).map(([key, value]) => {
                // イベント実行計画から日付指定で作成した場合、既存の日時を維持する
                if (
                  dateKeys.includes(key) &&
                  prev[key] != null &&
                  prev[key] !== ''
                ) {
                  return [key, prev[key]];
                }
                return [key, value];
              }),
            ),
          };
        });
        setEventsPlans(JSON.parse(response.data.events_plans));
        if (response.data.contents) {
          const contents = JSON.parse(response.data.contents);
          setContents(contents);
          const textContent = contents.find(
            (content) => content.content_type === 1,
          );
          if (textContent) {
            setBodyText(textContent.body);
          }
        }
      });
  }, [props]);

  return (
    <div>
      {page === 'entry' && (
        <EntryScreen
          title={titleText}
          onTitleChange={changeEntryTitle}
          onBack={() => window.history.back()}
          suggest={suggest}
          onPickSuggestion={(title) =>
            requestApplyEntryDraft({ title, body: '', source: 'ai' })
          }
          onRegenerate={() => fetchSuggestions(suggest.shown)}
          genre={genreLabel}
          onGenre={(label, skeleton) =>
            requestApplyEntryDraft(genreDraftFor(label, skeleton))
          }
          aiTheme={aiTheme}
          setAiTheme={setAiTheme}
          onAI={(theme) => generateFromTheme(theme)}
          onNext={(title) => commitEntryTitle(title)}
          onPastCopy={() => {
            openHistoryFrom('entry');
          }}
          showPastCopy={!!props.isShowHistoryButton && events.length > 0}
          saving={entrySaving}
          aiGenerating={aiGenerating}
          error={entryError}
          aiError={aiError}
          autoFocusTitle={entryAutoFocusPending.current}
          onFocusScrollForAndroid={onEntryTitleFocus}
        />
      )}
      {page === 0 || page === 'basic' || page === 'detail' ? (
        <Main
          props={props}
          page={page}
          historyEventId={historyEventId}
          setPage={setPageFromMain}
          event={event}
          circle={circle}
          usersCircles={usersCircles}
          setUsersCircles={setUsersCircles}
          setEvent={setEvent}
          setCircle={setCircle}
          eventsPlans={eventsPlans}
          setEventsPlans={setEventsPlans}
          contents={contents}
          setContents={setContents}
          bodyText={bodyText}
          setBodyText={setBodyText}
          titleText={titleText}
          setTitleText={setTitleText}
          isNewPolicyApplicable={props.isNewPolicyApplicable}
          reservationSummary={reservationSummary}
          onEditReservation={handleEditReservation}
          setSelectedPlace={setSelectedPlace}
          draftSource={draftSource}
          aiSuggested={aiSuggested}
          clearAiSuggested={clearAiSuggested}
          markAiAccepted={markAiAccepted}
        />
      ) : (
        ''
      )}
      {page === 1 ? (
        <History
          props={props}
          events={events}
          setHistoryEventId={setHistoryEventId}
          setEvent={setEvent}
          setEventsPlans={setEventsPlans}
          setContents={setContents}
          setBodyText={setBodyText}
          setTitleText={setTitleText}
          onBack={() =>
            setPage(
              historyDestination({ origin: historyOriginPage, copied: false }),
            )
          }
          onCopied={() => {
            // F1-11 コピー成功後だけ、コピー由来へ切り替えて基本ページへ進む。
            genreHint.current = null;
            setGenreLabel(null);
            setDraftSource('copy');
            setAiSuggested({ title: false, body: false });
            setPage(
              historyDestination({ origin: historyOriginPage, copied: true }),
            );
          }}
        />
      ) : (
        ''
      )}
      {page === 3 && selectedPlace && (
        <PlaceReservation
          place={selectedPlace}
          eventId={event.id}
          eventEndDefault={
            reservationSummary?.end_at || event.event_end_datetime
          }
          eventDateDefault={reservationSummary?.start_at || event.event_date}
          initialData={reservationSummary || {}}
          onBack={() => setPage(returnPage)}
          onFinish={(summary) => {
            setReservationSummary(summary);
            setEvent((prev) => ({
              ...prev,
              ...(prev.event_date
                ? {}
                : {
                    event_date: summary.start_at,
                    event_end_datetime: summary.end_at,
                  }),
              place: selectedPlace?.place || prev.place,
            }));
            setPage(returnPage);
          }}
        />
      )}
      <ModalAlert
        ref={aiRateLimitModalRef}
        id="modal-entry-ai-rate-limit-error"
        title="自動生成失敗"
        content="1日の利用数を超過しました。24時間後にもう一度お試しください。"
      />
      <ModalConfirm
        ref={overwriteConfirmModalRef}
        content="入力中のイベント名を置き換えますか？"
        approveFunc={() => {
          const pendingDraft = pendingEntryDraftRef.current;
          pendingEntryDraftRef.current = null;
          if (pendingDraft) applyEntryDraft(pendingDraft);
        }}
        rejectFunc={() => {
          pendingEntryDraftRef.current = null;
        }}
      />
    </div>
  );
};

export default EventEdit;
